import React from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@codes/ui-kit/components/ui/card';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

interface DailyUsageChartProps {
    data: any[];
}

export const DailyUsageChart: React.FC<DailyUsageChartProps> = ({ data }) => {
    const { t } = useTranslation();

    if (data.length === 0) {
        return null;
    }

    const customXAxisTick = (tickProps: any) => {
        const { x, y, payload, index } = tickProps;
        const middleIndex = Math.floor(data.length / 2);

        if (index === 0 || index === middleIndex || index === data.length - 1) {
            const [month, day] = payload.value.split('-');
            const formattedDate = `${parseInt(month)}월 ${parseInt(day)}일`;
            return (
                <text x={x} y={y} dy={16} textAnchor="middle" fill="#666" fontSize={12}>
                    {formattedDate}
                </text>
            );
        }
        return null;
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-xl font-bold">{t('logs.charts.dailyUsage')}</CardTitle>
                <CardDescription className="text-sm text-gray-600">{t('logs.charts.dailyUsageDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <defs>
                            <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1} />
                            </linearGradient>
                            <linearGradient id="colorAPICalls" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="date" tick={customXAxisTick} interval={0} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                            }}
                        />
                        <Legend />
                        <Area
                            type="monotone"
                            dataKey="apiCalls"
                            stroke="#10B981"
                            fillOpacity={1}
                            fill="url(#colorAPICalls)"
                            strokeWidth={2}
                            name={t('logs.charts.apiCallsLabel')}
                        />
                        <Area
                            type="monotone"
                            dataKey="sessions"
                            stroke="#3B82F6"
                            fillOpacity={1}
                            fill="url(#colorSessions)"
                            strokeWidth={2}
                            name={t('logs.charts.sessionsLabel')}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};
